import styles from "./DadosLoja.module.scss";
import React,{useEffect} from "react";
import {Controller,useForm} from "react-hook-form";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {Input} from "shared/components/inputs/Input";
import {PriceInput} from "shared/components/inputs/PriceInput";
import {maskCpfCnpj,maskTelefone} from "shared/utils/masks";
import {useCategoriasLoja} from "shared/hooks/useCategoriasLoja";

const DIAS_SEMANA=[
  {value:"MON",label:"Segunda"},
  {value:"TUE",label:"Terça"},
  {value:"WED",label:"Quarta"},
  {value:"THU",label:"Quinta"},
  {value:"FRI",label:"Sexta"},
  {value:"SAT",label:"Sábado"},
  {value:"SUN",label:"Domingo"},
] as const;

type DiaSemana=(typeof DIAS_SEMANA)[number]["value"];

type HorarioDia={
  openingTime:string;
  closingTime:string;
};

export type BusinessHoursSchedule={
  MON:HorarioDia|null;
  TUE:HorarioDia|null;
  WED:HorarioDia|null;
  THU:HorarioDia|null;
  FRI:HorarioDia|null;
  SAT:HorarioDia|null;
  SUN:HorarioDia|null;
};

const horarioPadrao:HorarioDia={
  openingTime:"09:00",
  closingTime:"18:00",
};

const criarSchedulePadrao=():BusinessHoursSchedule=>({
  MON:{...horarioPadrao},
  TUE:{...horarioPadrao},
  WED:{...horarioPadrao},
  THU:{...horarioPadrao},
  FRI:{...horarioPadrao},
  SAT:{...horarioPadrao},
  SUN:null,
});

const scheduleSchema=z.object({
  MON:z.object({
    openingTime:z.string(),
    closingTime:z.string(),
  }).nullable(),
  TUE:z.object({
    openingTime:z.string(),
    closingTime:z.string(),
  }).nullable(),
  WED:z.object({
    openingTime:z.string(),
    closingTime:z.string(),
  }).nullable(),
  THU:z.object({
    openingTime:z.string(),
    closingTime:z.string(),
  }).nullable(),
  FRI:z.object({
    openingTime:z.string(),
    closingTime:z.string(),
  }).nullable(),
  SAT:z.object({
    openingTime:z.string(),
    closingTime:z.string(),
  }).nullable(),
  SUN:z.object({
    openingTime:z.string(),
    closingTime:z.string(),
  }).nullable(),
});

const schema=z.object({
  loja:z.string().min(2,"Nome da loja é obrigatório"),
  company_name:z.string().min(2,"Razão Social é obrigatória"),
  segmento:z.string().min(1,"Tipo de loja é obrigatório"),
  cnpj:z.string().min(14,"CNPJ inválido"),
  telefone:z.string().min(10,"Telefone inválido"),
  email:z.string().email("E-mail inválido"),
  annual_revenue:z.number({
    required_error:"Ganhos anuais são obrigatórios",
    invalid_type_error:"Informe um valor válido",
  }).positive("Ganhos anuais devem ser maiores que zero"),
  descricao:z.string().optional(),
  businessHoursSchedule:scheduleSchema,
});

export type InformacoesLojaFormData=z.infer<typeof schema>;

interface InformacoesLojaProps{
  onNext?:(data:InformacoesLojaFormData)=>void;
  defaultValues?:Partial<InformacoesLojaFormData>;
  modoEdicao?:boolean;
  lojaId?:string;
  onSalvo?:(dados:InformacoesLojaFormData)=>void;
}

export const DadosLoja:React.FC<InformacoesLojaProps>=({
  onNext,
  defaultValues,
  modoEdicao=false,
  lojaId,
  onSalvo,
})=>{
  const {categorias}=useCategoriasLoja();

  const scheduleInicial=
    defaultValues?.businessHoursSchedule??criarSchedulePadrao();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState:{errors},
  }=useForm<InformacoesLojaFormData>({
    resolver:zodResolver(schema),
    defaultValues:{
      loja:defaultValues?.loja??"",
      company_name:defaultValues?.company_name??"",
      segmento:defaultValues?.segmento?String(defaultValues.segmento):"",
      cnpj:defaultValues?.cnpj?maskCpfCnpj(defaultValues.cnpj):"",
      telefone:defaultValues?.telefone?maskTelefone(defaultValues.telefone):"",
      email:defaultValues?.email??"",
      annual_revenue:defaultValues?.annual_revenue??0,
      descricao:defaultValues?.descricao??"",
      businessHoursSchedule:scheduleInicial,
    },
  });

  useEffect(()=>{
    if(!defaultValues?.segmento)return;

    const segmento=String(defaultValues.segmento);

    const categoriaExiste=categorias.some(
      (categoria)=>String(categoria.id)===segmento
    );

    if(categoriaExiste){
      setValue("segmento",segmento,{
        shouldValidate:false,
        shouldDirty:false,
      });
    }
  },[categorias,defaultValues?.segmento,setValue]);

  useEffect(()=>{
    if(!defaultValues?.businessHoursSchedule)return;

    setValue(
      "businessHoursSchedule",
      defaultValues.businessHoursSchedule,
      {
        shouldValidate:false,
        shouldDirty:false,
      }
    );
  },[defaultValues?.businessHoursSchedule,setValue]);

  const schedule=watch("businessHoursSchedule");

  const alternarDia=(dia:DiaSemana)=>{
    const atual=schedule?.[dia];

    if(atual){
      setValue(`businessHoursSchedule.${dia}`,null,{
        shouldValidate:true,
        shouldDirty:true,
      });
      return;
    }

    setValue(
      `businessHoursSchedule.${dia}`,
      {...horarioPadrao},
      {
        shouldValidate:true,
        shouldDirty:true,
      }
    );
  };

  const onSubmit=(data:InformacoesLojaFormData)=>{
    if(modoEdicao&&lojaId){
      onSalvo?.(data);
      return;
    }

    onNext?.(data);
  };

  return(
    <form
      id="informacoes-loja-form"
      onSubmit={handleSubmit(onSubmit)}
      className={styles.form}
    >
      <div className={styles.containerLabel}>
        <label className={`${styles.label} ${styles.required}`}>
          Nome da Loja
        </label>
        <Input
          type="text"
          name="loja"
          placeholder="Digite o nome da sua loja"
          register={register("loja")}
          error={errors.loja?.message}
          customClass={styles.customInput}
        />
      </div>

      <div className={styles.containerLabel}>
        <label className={`${styles.label} ${styles.required}`}>
          Razão Social
        </label>
        <Input
          type="text"
          name="company_name"
          placeholder="Digite a Razão Social"
          register={register("company_name")}
          error={errors.company_name?.message}
          customClass={styles.customInput}
        />
      </div>

      <div className={styles.containerLabel}>
        <label className={`${styles.label} ${styles.required}`}>
          Tipo de Loja
        </label>
        <Input
          type="select"
          name="segmento"
          placeholder="Qual o tipo do seu negócio"
          register={register("segmento")}
          error={errors.segmento?.message}
          customClass={styles.customSelect}
          options={categorias.map((categoria)=>({
            value:String(categoria.id),
            label:categoria.name,
          }))}
        />
      </div>

      <div className={styles.inputs2}>
        <div className={styles.containerLabel}>
          <label className={`${styles.label} ${styles.required}`}>
            CNPJ
          </label>
          <Input
            type="text"
            name="cnpj"
            placeholder="Digite o CNPJ da sua loja"
            register={register("cnpj",{
              onChange:(e)=>{
                e.target.value=maskCpfCnpj(e.target.value);
              },
            })}
            error={errors.cnpj?.message}
            inputMode="numeric"
            customClass={styles.customInputCNPJ}
          />
        </div>

        <div className={styles.containerLabel}>
          <label className={`${styles.label} ${styles.required}`}>
            Telefone / WhatsApp
          </label>
          <Input
            type="text"
            name="telefone"
            placeholder="Digite seu telefone"
            register={register("telefone",{
              onChange:(e)=>{
                e.target.value=maskTelefone(e.target.value);
              },
            })}
            error={errors.telefone?.message}
            inputMode="numeric"
            customClass={styles.customInput}
          />
        </div>
      </div>

      <div className={styles.containerLabel}>
        <label className={`${styles.label} ${styles.required}`}>
          E-mail Comercial
        </label>
        <Input
          type="email"
          name="email"
          placeholder="Informe seu e-mail comercial"
          register={register("email")}
          error={errors.email?.message}
          customClass={styles.customInput}
        />
      </div>

      <div className={styles.containerLabel}>
        <label className={`${styles.label} ${styles.required}`}>
          Ganhos Anuais
        </label>
        <Controller
          name="annual_revenue"
          control={control}
          render={({field})=>(
            <PriceInput
              value={(field.value??0)*100}
              onChange={(centavos)=>{
                field.onChange(centavos/100);
              }}
              placeholder="0,00"
              error={errors.annual_revenue?.message}
              customClass={styles.customInput}
            />
          )}
        />
      </div>

      <div className={styles.containerLabel}>
        <label className={styles.label}>
          Descrição
        </label>
        <Input
          type="text"
          name="descricao"
          placeholder="Fale sobre sua loja"
          register={register("descricao")}
          error={errors.descricao?.message}
          customClass={styles.customInput}
        />
      </div>

      <div className={`${styles.containerLabel} ${styles.funcionamento}`}>
        <label className={`${styles.label} ${styles.required}`}>
          Horários de funcionamento
        </label>

        <div className={styles.horariosSemana}>
          {DIAS_SEMANA.map((dia)=>{
            const horario=schedule?.[dia.value];
            const aberto=!!horario;

            return(
              <div
                key={dia.value}
                className={`${styles.horarioDia} ${
                  aberto
                    ? styles.horarioDiaAberto
                    : styles.horarioDiaFechado
                }`}
              >
                <button
                  type="button"
                  className={`${styles.diaButton} ${
                    aberto?styles.diaSelecionado:""
                  }`}
                  onClick={()=>alternarDia(dia.value)}
                  aria-pressed={aberto}
                >
                  {dia.label}
                </button>

                {aberto&&horario?(
                  <div className={styles.horarioRow}>
                    <Input
                      type="time"
                      name={`businessHoursSchedule.${dia.value}.openingTime`}
                      register={register(
                        `businessHoursSchedule.${dia.value}.openingTime` as const
                      )}
                      error={
                        errors.businessHoursSchedule?.[dia.value]
                          ?.openingTime?.message
                      }
                      customClass={styles.customInput}
                    />

                    <span className={styles.ate}>
                      até
                    </span>

                    <Input
                      type="time"
                      name={`businessHoursSchedule.${dia.value}.closingTime`}
                      register={register(
                        `businessHoursSchedule.${dia.value}.closingTime` as const
                      )}
                      error={
                        errors.businessHoursSchedule?.[dia.value]
                          ?.closingTime?.message
                      }
                      customClass={styles.customInput}
                    />
                  </div>
                ):(
                  <span className={styles.diaFechado}>
                    Fechado
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </form>
  );
};
